import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArchiveOutlined as ArchiveOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import type { StaffRole } from '../constants/staff';
import { useAuth } from '../auth/AuthContext';
import RoleChip from './RoleChip';
import GoogleConsoleStaffDialog from './GoogleConsoleStaffDialog';

export type StaffListItem = {
  id: number;
  email: string;
  display_name: string;
  role: StaffRole;
  avatar_url: string | null;
  is_active: boolean;
  is_developer: boolean;
  /** google | email_password */
  auth_method: 'google' | 'email_password';
  cognito_linked: boolean;
  created_at: string;
};

const StaffList: React.FC = () => {
  const navigate = useNavigate();
  const { staffProfile } = useAuth();
  const [rows, setRows] = useState<StaffListItem[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmArchive, setConfirmArchive] = useState<StaffListItem | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [googleAfterArchiveOpen, setGoogleAfterArchiveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<StaffListItem[]>('/api/staff');
      setRows(res.data);
    } catch {
      setError('職員一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirmArchive = async () => {
    if (!confirmArchive) return;
    setArchiving(true);
    setError('');
    try {
      await apiClient.patch(`/api/staff/${confirmArchive.id}`, { is_active: false });
      setConfirmArchive(null);
      setGoogleAfterArchiveOpen(true);
      await load();
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setError(String(e.response.data.error));
      } else {
        setError('アーカイブに失敗しました');
      }
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <GoogleConsoleStaffDialog
        open={googleAfterArchiveOpen}
        mode="afterRemove"
        onClose={() => setGoogleAfterArchiveOpen(false)}
      />
      <Dialog
        open={Boolean(confirmArchive)}
        onClose={() => !archiving && setConfirmArchive(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>職員をアーカイブしますか？</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          {confirmArchive ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {confirmArchive.display_name}（{confirmArchive.email}）をアーカイブします。
              <br />
              職員の投稿などの記録は残ります。ログインと新規サインアップは無効化されます。
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setConfirmArchive(null)} disabled={archiving} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => void handleConfirmArchive()}
            disabled={archiving}
            sx={{ minWidth: 100 }}
          >
            アーカイブする
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          職員管理
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/staff/new')}>
          職員を追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>名前</TableCell>
              <TableCell>メール</TableCell>
              <TableCell>アカウント</TableCell>
              <TableCell>役職</TableCell>
              <TableCell>開発者</TableCell>
              <TableCell>紐付け</TableCell>
              <TableCell align="right" width={104}>
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>読み込み中…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>職員がまだ登録されていません</TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const isSelf = staffProfile?.id === r.id;
                const canArchive = r.is_active && !isSelf;
                const canEdit = r.is_active;
                return (
                  <TableRow
                    key={r.id}
                    sx={
                      !r.is_active
                        ? (theme) => ({
                            bgcolor: theme.palette.action.hover,
                            '& .MuiTableCell-root': {
                              color: theme.palette.text.secondary,
                            },
                          })
                        : undefined
                    }
                  >
                    <TableCell>{r.display_name}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>
                      {r.auth_method === 'email_password' ? 'それ以外' : 'Google'}
                    </TableCell>
                    <TableCell>
                      <RoleChip role={r.role} />
                    </TableCell>
                    <TableCell>{r.is_developer ? 'はい' : 'いいえ'}</TableCell>
                    <TableCell>{r.cognito_linked ? '紐付け済' : '未ログイン'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={canEdit ? '編集' : '退職済みの職員は編集できません'}>
                        <span>
                          <IconButton
                            aria-label="編集"
                            size="small"
                            disabled={!canEdit}
                            onClick={() => navigate(`/staff/${r.id}/edit`)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !r.is_active
                            ? '退職済みの職員です'
                            : isSelf
                              ? '自分自身はアーカイブできません'
                              : 'アーカイブ'
                        }
                      >
                        <span>
                          <IconButton
                            aria-label="アーカイブ"
                            size="small"
                            color="warning"
                            disabled={!canArchive}
                            onClick={() => setConfirmArchive(r)}
                          >
                            <ArchiveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StaffList;
